// src/utils/layout/buildSingleDocument.js
//
// Tek bir dokuman icin documentGroup olusturma — content, header, floor plan, special docs.

import * as THREE from 'three';
import { dxfDrawCore } from '../../../modules/dxf';
import { createDocumentHeaderParts } from '../../../utils/simpleTransformFunctions';
import { HEADER_HEIGHT } from './layoutConstants';
import { createDocumentContent } from './createDocumentContent';
import { prepareDocumentData } from './prepareDocumentData';
import { createOlePendingPromise } from './oleHandler';
import { setupVaziyetPlani, setupKatPlani, setupGroundFloor } from './setupFloorPlanInLayout';
import { buildKolonSemaMultiMeter, buildPanoDetayMultiMeter, buildEnerjiOdasi, buildBaslikFromTemplate } from './specialDocBuilders';
import { PROJECT_TYPES } from '../../../constants/projectTypes';

export function buildSingleDocument(docDef, params) {
  const { simpleSupply, floorPlans, projectType, mainGrounding, projectMeshSize,
    dispatch, updateFloorPlan, FLOOR_PLAN_TAB, energyRoomConfirmed,
    erData, panoDetayConfig, panelTypes, appStore } = params;

  const documentGroup = new THREE.Group();
  // Her documentGroup'a docName ekle \u2014 useDocumentMove (M drag) hook'u raycaster
  // hit'inden bu ad\u0131 okuyup hangi dok\u00fcman se\u00e7ildi\u011fini anlar. Kat Plan\u0131 i\u00e7in
  // floorPlanId de korunuyor.
  documentGroup.userData = {
    ...documentGroup.userData,
    docName: docDef.name,
    ...(docDef.name === 'Kat Plan\u0131' && docDef.floorPlanId ? { floorPlanId: docDef.floorPlanId } : {}),
  };

  // Data + content
  const data = prepareDocumentData(docDef, { simpleSupply, floorPlans, projectType, mainGrounding });
  const docContent = createDocumentContent(docDef.name, docDef, data, projectType);
  docDef.w = docContent.width;
  docDef.h = docContent.height;

  // Header
  if (docDef.name !== 'Baslik' && docDef.name !== 'Notlar' && docDef.name !== 'Sembol Listesi' && docDef.hasFrame !== false) {
    const headerParts = createDocumentHeaderParts(docDef.displayName || docDef.name, docContent.width, docContent.height, HEADER_HEIGHT, { drawFrame: true });
    if (docContent.result) docContent.result.push(...headerParts);
    else docContent.result = headerParts;
  }

  // OLE pending
  const olePromise = createOlePendingPromise(docContent, docDef, documentGroup, projectType);

  // DXF content render
  if (docContent.result?.length > 0 || docContent._yukCetveliOlePending || docContent._topraklamaHesabiOlePending) {
    const contentGroup = new THREE.Group();
    if (docContent.result?.length > 0) dxfDrawCore(contentGroup, docContent.result, [], "scene");
    if (contentGroup.children.length > 0) {
      const bbox = new THREE.Box3().setFromObject(contentGroup);
      // X de normalize: içeriğin SOL kenarını documentGroup origin'ine (x=0) çek.
      // Önceden sadece Y normalize ediliyordu (X=0) → içerik minX'i 0 değilse sol
      // kenar girintili kalıyordu; OLE/Kolon/floor-plan sol=0 olduğu için dokümanlar
      // arasında "girinti çıkıntılı" sol hizalama oluşuyordu. -bbox.min.x ile hepsi hizalı.
      contentGroup.position.set(-bbox.min.x, -bbox.max.y, 0);
      const actualH = bbox.max.y - bbox.min.y;
      if (actualH > Math.max(50, docDef.h * 0.3)) docDef.h = Math.ceil(actualH);
      documentGroup.add(contentGroup);
    } else if (olePromise) {
      documentGroup.add(contentGroup);
    }
  }

  // Floor plan clones
  if (docDef.name === "Vaziyet Plan\u0131" && docDef.group) setupVaziyetPlani(docDef, documentGroup);
  if (docDef.name === "Kat Plan\u0131" && docDef.group) {
    const savedFpGroupPos = appStore?.getState()?.utils?.fpGroupLocalPositions?.[docDef.floorPlanId] || null;
    setupKatPlani(docDef, documentGroup, projectMeshSize, dispatch, updateFloorPlan, FLOOR_PLAN_TAB, savedFpGroupPos);
  }
  if (docDef.name === "Ground Floor" && docDef.group) setupGroundFloor(docDef, documentGroup);

  // Special documents
  if (docDef.name === "Kolon \u015eemas\u0131" && projectType === PROJECT_TYPES.MULTI_METER) buildKolonSemaMultiMeter(docDef, documentGroup, simpleSupply, floorPlans, panelTypes);
  if (docDef.name === "Pano Detay" && projectType === PROJECT_TYPES.MULTI_METER && panoDetayConfig) buildPanoDetayMultiMeter(docDef, documentGroup, panoDetayConfig);
  if (docDef.name === "Enerji Odasi" && energyRoomConfirmed) buildEnerjiOdasi(docDef, documentGroup, erData);
  if (docDef.name === 'Baslik') buildBaslikFromTemplate(docDef, documentGroup, appStore);

  // Empty check
  const isEmpty = projectType === PROJECT_TYPES.MULTI_METER &&
    (docDef.name === 'Pano Detay' || docDef.name === 'Kolon \u015eemas\u0131') &&
    documentGroup.children.length === 0;

  return { documentGroup, olePromise, isEmpty };
}
